import { useMemo } from "react";
import useSWR from "swr";
import { PICKER_REPORT_MAX_DAYS, WAVE_STATUS } from "../../constants/PickingConstant";
import { daysBetween } from "../../functions/pickDay";
import { sortWaveSummaries, summariseWave } from "../../functions/pickerStats";
import { fetchWaveEvents } from "./usePickResults";
import { fetchWaves } from "./usePickWaves";

/**
 * The rounds of a period with their events, summarised for the picker report
 * (functions/pickerStats). A report and not a live view: read once per period, again on
 * "Vernieuwen". Only rounds that were started have events; open and cancelled ones are left out.
 */

export const fetchPickerReport = async (days) => {
  const waves = (await Promise.all(days.map((day) => fetchWaves(day)))).flat();
  const started = waves.filter((wave) => wave.status === WAVE_STATUS.BUSY || wave.status === WAVE_STATUS.DONE);
  const events = await Promise.all(started.map((wave) => fetchWaveEvents(wave.waveId || wave.id)));
  return started.map((wave, index) => ({ wave, events: events[index] }));
};

const NO_WAVES = [];

/**
 * @param {string} from - first pick day, DD-MM-YYYY
 * @param {string} to - last pick day, DD-MM-YYYY
 * @returns {{ days, tooLong, waves, isLoading, error, refresh }} `waves` are summariseWave results
 */
export const usePickerReport = (from, to) => {
  const days = useMemo(() => daysBetween(from, to), [from, to]);
  const tooLong = days.length > PICKER_REPORT_MAX_DAYS;
  const { data, error, isLoading, mutate } = useSWR(
    days.length > 0 && !tooLong ? ["pickerReport", from, to] : null,
    () => fetchPickerReport(days),
    { revalidateOnFocus: false },
  );
  const waves = useMemo(
    () => (data ? sortWaveSummaries(data.map(({ wave, events }) => summariseWave(wave, events))) : NO_WAVES),
    [data],
  );
  return { days, tooLong, waves, isLoading, error, refresh: mutate };
};
